<template>
  <section
    class="tracksheet-run-passages q-mt-md"
    data-testid="tracksheet-run-passages"
  >
    <div class="row items-center q-gutter-sm q-pb-sm">
      <div class="text-subtitle1 text-weight-medium">Passages on this run</div>
      <q-badge
        color="blue-brand"
        text-color="white"
        data-testid="tracksheet-run-passages-count"
      >
        {{ displayedPassages.length }}
      </q-badge>
      <q-chip
        v-if="selectedMarkerId != null"
        removable
        dense
        color="blue-brand"
        text-color="white"
        icon="place"
        class="tracksheet-run-passages__marker-chip"
        data-testid="tracksheet-run-passages-marker-chip"
        @remove="$emit('clear-marker')"
      >
        {{ markerChipLabel }}
      </q-chip>
      <q-space />
      <q-btn
        flat
        round
        dense
        icon="refresh"
        color="blue-brand"
        :loading="loading"
        data-testid="tracksheet-run-passages-refresh"
        @click="fetchPassages"
      >
        <q-tooltip>Refresh passages</q-tooltip>
      </q-btn>
    </div>

    <div v-if="errorMsg" class="q-pb-sm">
      <q-badge color="red">{{ errorMsg }}</q-badge>
    </div>

    <div
      class="row items-start q-gutter-sm q-pb-sm"
      data-testid="tracksheet-run-passages-filter"
    >
      <q-input
        v-model="filterFrom"
        dense
        filled
        mask="####-##-## ##:##"
        label="From (UTC)"
        hint="YYYY-MM-DD HH:mm"
        style="width: 190px"
        data-testid="tracksheet-run-passages-from"
      >
        <template #append>
          <q-icon name="event" class="cursor-pointer">
            <q-popup-proxy transition-show="scale" transition-hide="scale">
              <div class="row no-wrap">
                <q-date v-model="filterFrom" mask="YYYY-MM-DD HH:mm" />
                <q-time
                  v-model="filterFrom"
                  mask="YYYY-MM-DD HH:mm"
                  format24h
                />
              </div>
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
      <q-input
        v-model="filterTo"
        dense
        filled
        mask="####-##-## ##:##"
        label="To (UTC)"
        hint="YYYY-MM-DD HH:mm"
        style="width: 190px"
        data-testid="tracksheet-run-passages-to"
      >
        <template #append>
          <q-icon name="event" class="cursor-pointer">
            <q-popup-proxy transition-show="scale" transition-hide="scale">
              <div class="row no-wrap">
                <q-date v-model="filterTo" mask="YYYY-MM-DD HH:mm" />
                <q-time v-model="filterTo" mask="YYYY-MM-DD HH:mm" format24h />
              </div>
            </q-popup-proxy>
          </q-icon>
        </template>
      </q-input>
      <q-toggle
        v-model="filterGreenMl"
        dense
        label="Green ML only"
        color="green"
        class="q-mt-sm"
        data-testid="tracksheet-run-passages-green-ml"
      />
      <q-btn
        flat
        dense
        no-caps
        label="Clear"
        color="blue-brand"
        class="q-mt-xs"
        :disable="!filterFrom && !filterTo && !filterGreenMl"
        data-testid="tracksheet-run-passages-clear"
        @click="clearFilters"
      />
    </div>
    <div
      v-if="timeFilterRange.error"
      class="q-pb-sm text-negative text-caption"
      data-testid="tracksheet-run-passages-filter-error"
    >
      {{ timeFilterRange.error }}
    </div>

    <PassagesTable
      ref="passagesTable"
      v-model:pagination="pagination"
      :rows="displayedPassages"
      :columns="columns"
      :row-key="rowKey"
      :sort-method="keepOrder"
      :loading="loading"
      :no-data-label="noDataLabel"
      :dialog-source="dialogSource"
      :pig-run-id="pigRunId"
      :active="chartStepsHere"
      :row-opens-chart="isNarrow"
      data-testid="tracksheet-run-passages-table"
    />
  </section>
</template>

<script lang="ts">
/**
 * EN-1851: every passage on the run, below the tracksheet. Sortable on every
 * data column (marker sorts by marker order), newest first by default, with
 * the View Passages dialog's waveform, verify and ML columns (PassagesTable).
 * Refreshes on SignalR passages from the run's devices, run refreshes and ML
 * results for the run, like the dialog. Selecting a tracksheet marker row
 * filters it to that marker (chip; removing the chip clears the selection).
 */
import { defineComponent, PropType } from 'vue';
import { EventBus } from '@/utils/EventBus';
import { IRunPassage } from '@/models/Passage';
import PigRunService from '@/services/PigRunService';
import PassagesTable from '@/components/PassagesTable.vue';
import { UtcRange, buildUtcRange } from '@/utils/HistoryFilter';
import {
  RunPassageColumn,
  RunPassageTimeZone,
  applyVerifiedPassage,
  filterRunPassages,
  formatRunPassageTime,
  runPassageRowKey,
  runPassageTimeLabel,
  shouldRefreshRunPassages,
  sortRunPassages,
} from '@/utils/RunPassages';
import { markerFilterChipLabel } from '@/utils/MarkerSelection';
import {
  TRACKSHEET_SECTION_SOURCE,
  respondsToSwitchPassage,
} from '@/utils/MarkerPassagesPopover';

/** A picker value counts once fully typed (YYYY-MM-DD HH:mm). */
const COMPLETE_FILTER_LENGTH = 16;
const REFRESH_DEBOUNCE_MS = 1500;
const REFRESH_MAX_WAIT_MS = 5000;
/** Columns kept on a phone-width screen (a row click opens the waveform). */
const MOBILE_COLUMNS: string[] = [
  RunPassageColumn.marker,
  RunPassageColumn.time,
  RunPassageColumn.verify,
  RunPassageColumn.ml,
];

export default defineComponent({
  name: 'TracksheetRunPassages',
  components: { PassagesTable },
  props: {
    pigRunId: { type: Number as PropType<number | null>, default: null },
    /** The run's device uids (incl. manual links): which passages refresh. */
    runUids: { type: Array as PropType<number[]>, default: () => [] },
    /** The selected tracksheet marker row: only its passages, or all. */
    selectedMarkerId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    selectedMarkerName: { type: String, default: '' },
    /** The tracksheet's Pass Time setting (UTC / Local); no own toggle. */
    timeZone: {
      type: String as PropType<RunPassageTimeZone>,
      default: 'utc',
    },
  },
  emits: ['clear-marker', 'passages-changed'],
  data() {
    return {
      passages: [] as IRunPassage[],
      /** The run whose passages are loaded (the popover waits for it). */
      loadedPigRunId: null as number | null,
      dialogSource: TRACKSHEET_SECTION_SOURCE,
      loading: false,
      errorMsg: '',
      requestVersion: 0,
      filterFrom: '',
      filterTo: '',
      filterGreenMl: false,
      pagination: {
        sortBy: RunPassageColumn.time as string | null,
        descending: true,
        page: 1,
        rowsPerPage: 10,
      },
      unsubscribeAction: null as (() => void) | null,
      refreshDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      refreshMaxWaitTimer: null as ReturnType<typeof setTimeout> | null,
    };
  },
  computed: {
    timeFilterRange(): UtcRange {
      const complete = (v: string) =>
        v && v.length >= COMPLETE_FILTER_LENGTH ? v : '';
      return buildUtcRange(complete(this.filterFrom), complete(this.filterTo));
    },
    /** Filtered and sorted here so the chart's next / previous follows it. */
    displayedPassages(): IRunPassage[] {
      const filtered = filterRunPassages(this.passages, {
        pipelineSectionMarkerId: this.selectedMarkerId,
        range: this.timeFilterRange,
        greenMlOnly: this.filterGreenMl,
      });
      return sortRunPassages(
        filtered,
        this.pagination.sortBy,
        this.pagination.descending
      );
    },
    markerChipLabel(): string {
      return markerFilterChipLabel(this.selectedMarkerName);
    },
    noDataLabel(): string {
      if (this.loading) return 'Loading passages';
      return this.passages.length > 0
        ? 'No passages match the filter'
        : 'No passages on this run yet';
    },
    /** Phone width: fewer columns; a row click opens the waveform. */
    isNarrow(): boolean {
      return this.$q.screen.lt.md;
    },
    columns(): any[] {
      const zone = this.timeZone;
      const all = [
        {
          name: RunPassageColumn.marker,
          label: 'Marker',
          field: 'markerName',
          align: 'left' as const,
          sortable: true,
        },
        {
          name: RunPassageColumn.time,
          label: runPassageTimeLabel(zone),
          field: 'passageTimeStamp',
          align: 'left' as const,
          sortable: true,
          format: (value: string) => formatRunPassageTime(value, zone),
          // Date over time, so the ML column fits the drawer and a phone.
          classes: 'tracksheet-run-passages__time',
          headerClasses: 'tracksheet-run-passages__time',
        },
        {
          name: RunPassageColumn.uid,
          label: 'UID',
          field: 'uid',
          align: 'left' as const,
          sortable: true,
        },
        {
          name: RunPassageColumn.detectionCount,
          label: 'Det Count',
          field: 'detectionCount',
          align: 'left' as const,
          sortable: true,
          headerClasses: 'tracksheet-run-passages__wrap',
        },
        {
          name: RunPassageColumn.waveforms,
          label: 'Waveforms',
          field: 'graph-data',
          align: 'left' as const,
        },
        {
          name: RunPassageColumn.verify,
          label: 'Verify',
          field: 'verify-data',
          align: 'center' as const,
          sortable: true,
        },
        {
          name: RunPassageColumn.ml,
          label: 'ML',
          field: 'machine-learning-data',
          align: 'center' as const,
          sortable: true,
          // The MAG / ELF badges stack, so the column fits the drawer.
          classes: 'tracksheet-run-passages__ml',
        },
      ];
      if (!this.isNarrow) return all;
      return all.filter((c) => MOBILE_COLUMNS.includes(c.name));
    },
    /** The chart's next / previous steps through these rows when opened here. */
    chartStepsHere(): boolean {
      return respondsToSwitchPassage(
        this.dialogSource,
        this.$store.state.passage?.passageDialogSource
      );
    },
  },
  watch: {
    pigRunId: {
      immediate: true,
      handler() {
        this.passages = [];
        this.loadedPigRunId = null;
        this.errorMsg = '';
        this.filterFrom = '';
        this.filterTo = '';
        this.resetPage();
        this.cancelRefresh();
        this.fetchPassages();
      },
    },
    timeFilterRange() {
      this.resetPage();
    },
    filterGreenMl() {
      this.resetPage();
    },
    selectedMarkerId() {
      this.resetPage();
    },
    /** EN-1851: the tracksheet popover reuses these rows (no request). */
    passages() {
      this.emitPassages();
    },
    loadedPigRunId() {
      this.emitPassages();
    },
  },
  created() {
    this.unsubscribeAction = this.$store.subscribeAction((action: any) => {
      if (shouldRefreshRunPassages(action, this.pigRunId, this.runUids)) {
        this.scheduleRefresh();
      }
    });
  },
  mounted() {
    EventBus.on('passage-verified', this.handlePassageVerified as any);
  },
  beforeUnmount() {
    EventBus.off('passage-verified', this.handlePassageVerified as any);
    if (this.unsubscribeAction) this.unsubscribeAction();
    this.cancelRefresh();
  },
  methods: {
    emitPassages() {
      this.$emit('passages-changed', {
        passages: this.passages,
        loaded:
          this.loadedPigRunId !== null && this.loadedPigRunId === this.pigRunId,
      });
    },
    rowKey(row: IRunPassage): string {
      return runPassageRowKey(row);
    },
    /** displayedPassages is already sorted; q-table must keep that order. */
    keepOrder(rows: readonly IRunPassage[]): IRunPassage[] {
      return rows as IRunPassage[];
    },
    resetPage() {
      this.pagination = { ...this.pagination, page: 1 };
    },
    clearFilters() {
      this.filterFrom = '';
      this.filterTo = '';
      this.filterGreenMl = false;
    },
    async fetchPassages() {
      const pigRunId = this.pigRunId;
      const version = ++this.requestVersion;
      if (!pigRunId) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await PigRunService.getPigRunPassages(pigRunId);
        if (version !== this.requestVersion) return;
        this.passages = Array.isArray(response.data) ? response.data : [];
        this.loadedPigRunId = pigRunId;
        this.errorMsg = '';
      } catch (error) {
        if (version !== this.requestVersion) return;
        console.error('Failed to fetch run passages:', error);
        this.errorMsg = 'Failed to load passages';
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    },
    scheduleRefresh() {
      // Trailing-edge debounce with a max wait, as the View Passages dialog.
      if (this.refreshDebounceTimer) clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = setTimeout(
        this.flushRefresh,
        REFRESH_DEBOUNCE_MS
      );
      if (!this.refreshMaxWaitTimer) {
        this.refreshMaxWaitTimer = setTimeout(
          this.flushRefresh,
          REFRESH_MAX_WAIT_MS
        );
      }
    },
    cancelRefresh() {
      if (this.refreshDebounceTimer) clearTimeout(this.refreshDebounceTimer);
      if (this.refreshMaxWaitTimer) clearTimeout(this.refreshMaxWaitTimer);
      this.refreshDebounceTimer = null;
      this.refreshMaxWaitTimer = null;
    },
    flushRefresh() {
      this.cancelRefresh();
      this.fetchPassages();
    },
    handlePassageVerified({ passage }: any) {
      if (!passage) return;
      this.passages = applyVerifiedPassage(this.passages, passage);
    },
  },
});
</script>

<style scoped>
/*
 * EN-1851: Default.vue's .bg-blue-brand has no !important, so Quasar's own
 * .q-chip background (#e0e0e0) won and the white label was unreadable. The
 * brand colour again, white on #24337a (contrast 11.6:1).
 */
.tracksheet-run-passages__marker-chip {
  background: #24337a !important;
}
.tracksheet-run-passages :deep(.tracksheet-run-passages__time) {
  white-space: normal;
  min-width: 88px;
  max-width: 112px;
}
.tracksheet-run-passages :deep(.tracksheet-run-passages__wrap) {
  white-space: normal;
  max-width: 60px;
}
.tracksheet-run-passages :deep(.tracksheet-run-passages__ml) {
  white-space: normal;
  min-width: 72px;
  max-width: 84px;
}
.tracksheet-run-passages :deep(.tracksheet-run-passages__ml > *) {
  margin: 1px 0;
}
</style>
